import React, {useCallback, useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, styled} from '@mui/material';
import ProjectCard, {
  ProjectCardSkeleton,
} from '@contentlab/components/project-card';
import {
  PROJECT_CARD_MAX_WIDTH,
  PROJECT_CARD_MIN_WIDTH,
} from '@contentlab/components/project-card/constants';
import ProjectsEmptyState from '@contentlab/components/projects-empty-state/ProjectsEmptyState';
import {
  PROJECT_CARDS_COLUMN_GAP,
  PROJECT_CARDS_ROW_GAP,
  PROJECTS_CONTENT_MAX_WIDTH,
} from '@contentlab/config/constants';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProjectActions from '@contentlab/hooks/use-project-actions';
import projectsStore, {
  ProjectsStatusFilter,
  projectsStoreActions,
  ProjectsToolFilter,
} from '@contentlab/stores/projectsStore';
import ProjectAction from '@contentlab/types/ProjectAction';
import mapProjectToProjectProps from '@contentlab/utils/mapProjectToProjectProps';
import {SortId} from '@contentlab/views/contentlab/pages/projects-page/components/projects-page-header/ProjectsPageHeader';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import ElementInView from '@/components/element-in-view/ElementInView';
import Link from '@/components/link';
import useProjectsQuery from '@/hooks/content-studio/use-projects-query';
import ProjectsPageHeader from './components/projects-page-header';
import {
  convertSortIdToSorting,
  convertSortingFieldToSortBy,
  convertSortingToSortId,
  convertStatusFilterToProjectStatus,
  convertToolFilterToFeatureName,
} from './components/projects-page-header/utils';

const PROJECTS_CONTAINER_INLINE_PADDING = 32;

const EmptyStateContainer = styled(Box)({
  display: 'flex',
  height: '640px',
  padding: '124px 40px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  boxSizing: 'border-box',
});

const PageContainer = styled(Box)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const ProjectsContainer = styled(Box)({
  boxSizing: 'border-box',
  gap: `${PROJECT_CARDS_ROW_GAP}px ${PROJECT_CARDS_COLUMN_GAP}px`,
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, 
    minmax(${PROJECT_CARD_MIN_WIDTH}px, min(${PROJECT_CARD_MAX_WIDTH}px, calc((100% - ${
      PROJECT_CARDS_COLUMN_GAP * 3
    }px) / 4))))`,
  justifyContent: 'center',
  paddingBottom: '24px',
  paddingInline: `${PROJECTS_CONTAINER_INLINE_PADDING}px`,
  width: '100%',
  maxWidth: `calc(${PROJECTS_CONTENT_MAX_WIDTH}px + ${
    PROJECTS_CONTAINER_INLINE_PADDING * 2
  }px)`, // account for padding on each side
});

function ProjectsPage() {
  const {uiInteraction} = useUiInteractionEvent();

  const {filters, sorting} = projectsStore(
    useShallow((state) => ({
      filters: state.filters,
      sorting: state.sorting,
    }))
  );

  const {
    data: projects,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useProjectsQuery({
    sortBy: convertSortingFieldToSortBy(sorting.field),
    sortOrder: sorting.direction,
    pageSize: 12,
    featureFilter: convertToolFilterToFeatureName(filters.tool),
    statusFilter: convertStatusFilterToProjectStatus(filters.status),
  });

  const {onProjectActionClick, projectActionModals} = useProjectActions({
    interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS,
    flow_name: FLOW_NAMES.CONTENT_LAB.PROJECTS,
    onActionComplete: (action: ProjectAction) => {
      switch (action) {
        case ProjectAction.Rename:
        case ProjectAction.Delete:
          refetch();
          break;
        case ProjectAction.Download:
          break;
        default:
          throw new Error(`Unknown project action: ${action}`);
      }
    },
  });

  const projectCardsData = useMemo(() => {
    return projects.map(mapProjectToProjectProps);
  }, [projects]);

  const onSortChange = (sortId: SortId) => {
    uiInteraction({
      item_name: 'sort_projects',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECTS,
      interaction_details: sortId,
    });
    const currentSorting = convertSortIdToSorting(sortId);
    projectsStoreActions.setProjectsSorting(currentSorting);
  };

  const onToolsFilterChange = (filterTool: ProjectsToolFilter) => {
    uiInteraction({
      item_name: 'filter_projects_tool',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECTS,
      interaction_details: filterTool,
    });
    projectsStoreActions.setProjectsFilters({
      tool: filterTool,
    });
  };

  const onStatusFilterChange = (filterStatus: ProjectsStatusFilter) => {
    uiInteraction({
      item_name: 'filter_projects_status',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECTS,
      interaction_details: filterStatus,
    });
    projectsStoreActions.setProjectsFilters({
      status: filterStatus,
    });
  };

  const renderProjectCardSkeletons = useCallback(() => {
    return Array.from({length: 7}).map((_, index) => (
      <Box
        // eslint-disable-next-line react/no-array-index-key
        key={index}
      >
        <ProjectCardSkeleton data-testid={`project-card-skeleton-${index}`} />
      </Box>
    ));
  }, []);

  return (
    <PageContainer>
      <ProjectsPageHeader
        selectedToolFilter={filters.tool}
        selectedStatusFilter={filters.status}
        selectedSortId={convertSortingToSortId(sorting)}
        onSortChange={onSortChange}
        onToolsFilterChange={onToolsFilterChange}
        onStatusFilterChange={onStatusFilterChange}
      />
      {isLoading && (
        <ProjectsContainer>{renderProjectCardSkeletons()}</ProjectsContainer>
      )}
      {projectCardsData.length === 0 && !isLoading && (
        <EmptyStateContainer>
          <ProjectsEmptyState />
        </EmptyStateContainer>
      )}
      {projectCardsData.length > 0 && !isLoading && (
        <ProjectsContainer>
          {projectCardsData.map((project) => (
            <Link
              to={`${ROUTES.CONTENT_LAB}/project/${project.id}`}
              key={project.id}
            >
              <ProjectCard
                project={project}
                onActionsClick={(projectProps) => {
                  const interactionId = uuid();
                  uiInteraction({
                    item_name: 'more_actions',
                    interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS,
                    flow_name: FLOW_NAMES.CONTENT_LAB.PROJECTS,
                    asset_id: projectProps.id,
                    interaction_id: interactionId,
                  });
                }}
                onProjectActionClick={onProjectActionClick}
              />
            </Link>
          ))}
          {isFetchingNextPage ? renderProjectCardSkeletons() : null}
          <ElementInView
            onInView={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            height={1}
          />
        </ProjectsContainer>
      )}

      {projectActionModals}
    </PageContainer>
  );
}

export default ProjectsPage;
